/**
 * im12 桌面通知（OS 级 Web Notification）— 仅在页面【不可见】时兜底。
 *
 * 职责（与 app 内 toast/提示音分工）：
 *   - 页面 visible 时：交给 app 内 toast.imMessage + notificationSound（见 useImRealtime）。
 *   - 页面 hidden（最小化 / 切到别的标签页）时：app 内声音多半被浏览器后台标签节流/block，
 *     改走 OS 级系统通知（系统通知自带提示音），点击后聚焦窗口 + 跳到对应会话。
 *
 * 边界（写进规格·网页天花板）：
 *   - 浏览器进程【全关】时收不到任何通知 —— 不做 Web Push / Service Worker（手机 App 覆盖该场景）。
 *   - 用户拒绝授权 → 静默降级（回 app 内 toast，不再骚扰、不复问）。
 *   - 不支持 Notification 的浏览器 → 全 try/catch 兜底、静默跳过。
 */

const LS_PROMPTED = 'im_notify_prompted'; // 记「已引导过授权」，避免反复弹系统授权框

/** 当前环境是否支持 Notification API。 */
function supported(): boolean {
  try {
    return typeof window !== 'undefined' && 'Notification' in window;
  } catch {
    return false;
  }
}

/**
 * 首次进 /im 时引导一次系统通知授权（必须在用户手势 / 进页时调）。
 *   - 仅当 Notification.permission === 'default'（从未问过）且本地未引导过才 requestPermission。
 *   - 引导后（无论同意/拒绝）写 localStorage 标记，永不复问。
 *   - 拒绝 = 静默降级（后续走 app 内 toast）。
 */
export function ensurePermissionOnce(): void {
  if (!supported()) return;
  try {
    if (localStorage.getItem(LS_PROMPTED)) return; // 已引导过，不复问
    if (Notification.permission !== 'default') {
      // 已 granted 或 denied：无需再问，仅落标记避免下次进来再判一遍
      localStorage.setItem(LS_PROMPTED, '1');
      return;
    }
    localStorage.setItem(LS_PROMPTED, '1'); // 先落标记，防并发/重入重复弹框
    // requestPermission 在部分浏览器返回 Promise、老浏览器走 callback；统一 catch 兜底
    const r = Notification.requestPermission();
    if (r && typeof (r as any).catch === 'function') {
      (r as Promise<NotificationPermission>).catch(() => { /* 静默降级 */ });
    }
  } catch {
    /* 静默降级 */
  }
}

/**
 * 仅当页面【hidden】且已【granted】时弹 OS 系统通知。
 *   - tag 按会话去重：同一会话连续新消息只覆盖同一条系统通知、不堆叠刷屏。
 *   - 点击通知：window.focus() 聚焦窗口 + 执行 onClick（跳对应会话）。
 *   - 全 catch 兜底；任何不满足条件直接静默返回（让上游回退 app 内 toast）。
 *
 * @returns 是否真的弹出了系统通知（true=已交给 OS；false=未弹、上游应走 app 内提示）
 */
export function notifyIfHidden(
  title: string,
  body: string,
  onClick?: () => void,
  tag?: string,
): boolean {
  if (!supported()) return false;
  try {
    if (document.visibilityState !== 'hidden') return false;
    if (Notification.permission !== 'granted') return false;
    const n = new Notification(title, {
      body,
      tag: tag || 'im-notify', // 同 tag 覆盖去重
      icon: '/favicon.ico',
    });
    n.onclick = () => {
      try {
        window.focus();
        n.close();
        onClick?.();
      } catch {
        /* ignore */
      }
    };
    return true;
  } catch {
    return false;
  }
}
